import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";

const navLinks = [
  { label: "About", href: "/about", isRoute: true },

  {
    label: "Services",
    submenu: [
      {
        label: "Ovarian Cancer Treatment",
        href: "/services/ovarian-cancer-treatment",
      },
      {
        label: "Cervical Cancer Treatment",
        href: "/services/Cervical-cancer-treatment",
      },
      {
        label: "Uterine EndoMetrial Cancer Treatment",
        href: "/services/uterine-cancer-treatment",
      },
      {
        label: "Vaginal & Vulval Cancer Care",
        href: "/services/vaginal-vulval-cancer-care",
      },
      {
        label: "HIPEC Surgery",
        href: "/services/hipec-surgery",
      },
      {
        label: "Laparoscopic & Robotic Surgery",
        href: "/services/laparoscopic-robotic-surgery",
      },
      {
        label: "Robotics Surgery",
        href: "/services/robotics-surgery",
      },
      {
        label: "Cancer Screening & Preventive Oncology",
        href: "/services/cancer-screening",
      },
      {
        label: "Non Cancer",
        href: "/services/non-cancer",
      },
    ],
  },

  { label: "Specialities", href: "/specialities", isRoute: true },
  { label: "Hospitals", href: "/hospitals", isRoute: true },
  { label: "Testimonials", href: "/testimonials", isRoute: true },
  //{ label: "FAQ", href: "/faq", isRoute: true },
  { label: "Contact", href: "/contactpage", isRoute: true },
];

export default function Header() {
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);

    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F9F9F6]/90 border-b border-[#1E3F33]/10"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 flex items-center justify-between h-16 md:h-20">

        {/* Logo */}
        <button
          data-testid="header-logo"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 group"
        >
          <span className="font-serif text-[24px] md:text-3xl font-semibold text-[#fcba12 ] tracking-tight">
                  <img
          src="/images/logo.png"
          alt="OG Group"
          class="h-8 w-auto"
        />
          </span>
        </button>

        {/* Desktop Nav */}
        <nav
          className="hidden lg:flex items-center gap-8"
          data-testid="desktop-nav"
        >
          {navLinks.map((link) =>
            link.submenu ? (
              <div key={link.label} className="relative group">

                <button className="flex items-center gap-1 text-sm font-medium text-[#4A6359] hover:text-[#1E3F33] transition-colors">
                  {link.label}
                  <ChevronDown className="w-4 h-4" />
                </button>

                <div className="absolute top-full left-0 mt-3 w-72 bg-white rounded-2xl shadow-2xl border border-[#D8DDD9] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 overflow-hidden">

                  {link.submenu.map((sub) => (
                    <Link
                      key={sub.label}
                      to={sub.href}
                      className="block px-5 py-3 text-sm text-[#4A6359] hover:bg-[#F2EFE9] hover:text-[#1E3F33] transition-colors border-b border-[#F1F1F1] last:border-0"
                    >
                      {sub.label}
                    </Link>
                  ))}

                </div>
              </div>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="text-sm font-medium text-[#4A6359] hover:text-[#1E3F33] transition-colors"
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        {/* Desktop CTA */}
        <a
          href="tel:+916291000829"
          data-testid="header-call-btn"
          className="hidden lg:flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
        >
          <Phone className="w-4 h-4" />
          Call Now
        </a>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden p-2 text-[#1E3F33]"
        >
          {mobileOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-nav"
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-[#fff] border-t border-[#1E3F33]/10 shadow-2xl"
          >
            <div className="px-4 py-5 flex flex-col gap-3 max-h-[85vh] overflow-y-auto">

              {navLinks.map((link) =>
                link.submenu ? (

                  <div
                    key={link.label}
                    className="overflow-hidden"
                  >

                    <button
                      onClick={() =>
                        setOpenMenu(
                          openMenu === link.label ? null : link.label
                        )
                      }
                      className="w-full flex items-center justify-between px-4 py-4"
                    >
                      <span className="text-[15px] font-semibold text-[#1E3F33]">
                        {link.label}
                      </span>

                      <ChevronDown
                        className={`w-4 h-4 text-[#4A6359] transition-transform duration-300 ${
                          openMenu === link.label ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {openMenu === link.label && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden bg-[#FCFCFA]"
                        >

                          {link.submenu.map((sub) => (
                            <Link
                              key={sub.label}
                              to={sub.href}
                              onClick={() => setMobileOpen(false)}
                              className="block px-4 py-3 text-sm text-[#4A6359] hover:bg-[#F2EFE9] hover:text-[#1E3F33] transition-colors"
                            >
                              {sub.label}
                            </Link>
                          ))}

                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className=" px-2 py-2 text-[15px] font-medium text-[#4A6359] hover:text-[#1E3F33] hover:bg-[#F2EFE9] transition-all"
                  >
                    {link.label}
                  </Link>
                )
              )}

              {/* Mobile CTA */}
              <a
                href="tel:+916291000829"
                className="mt-3 flex items-center justify-center gap-2 bg-[#1E3F33] text-white py-4 rounded-2xl text-sm font-semibold shadow-md"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}